/**
 * Reviews Slider - Splide.js Integration
 * Initializes and manages the reviews carousel with responsive behavior
 */

if (typeof window.ReviewsSlider === 'undefined') {
class ReviewsSlider {
  constructor(element) {
    this.splideElement = element || document.querySelector('.section-real-reviews .splide');
    if (this.splideElement) {
      this.prevButton = this.splideElement.closest('.section-real-reviews').querySelector('.reviews-nav-prev');
      this.nextButton = this.splideElement.closest('.section-real-reviews').querySelector('.reviews-nav-next');
    }
    this.splide = null;
    this.init();
  }

  init() {
    if (!this.splideElement) {
      console.warn('Reviews Splide element not found');
      return;
    }

    // Read settings from data attributes (with sensible fallbacks)
    const gapDesktop = parseInt(this.splideElement.dataset.gap || '0', 10);
    const gapMobile = parseInt(this.splideElement.dataset.gapMobile || '0', 10);
    const perPageDesktopRaw = parseFloat(this.splideElement.dataset.perPage || '4');
    const perPageTabletRaw = parseFloat(this.splideElement.dataset.perPageTablet || '2');
    const perPageMobileRaw = parseFloat(this.splideElement.dataset.perPageMobile || '1');

    // Cap perPage to actual slide count so we never request more
    // than we have (avoids the broken-grid + clone glitch).
    const slideCount = this.splideElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)').length
      || this.splideElement.querySelectorAll('.splide__slide').length;
    const perPageDesktop = Math.min(perPageDesktopRaw, slideCount) || 1;
    const perPageTablet = Math.min(perPageTabletRaw, slideCount) || 1;
    const perPageMobile = Math.min(perPageMobileRaw, slideCount) || 1;

    // Loop only makes sense when there are more slides than visible columns
    const useLoop = slideCount > perPageDesktop;

    // Detect decimals — if any breakpoint uses a fractional perPage we need
    // autoWidth + manually-set slide widths (Splide doesn't support fractional perPage).
    const hasDecimal = (n) => n % 1 !== 0;
    this.fractionalMode = hasDecimal(perPageDesktopRaw) || hasDecimal(perPageTabletRaw) || hasDecimal(perPageMobileRaw);
    this.perPageDesktop = perPageDesktop;
    this.perPageTablet = perPageTablet;
    this.perPageMobile = perPageMobile;
    this.gapDesktop = gapDesktop;
    this.gapMobile = gapMobile;

    // Initialize Splide with custom options
    // (currentPerPage() reads the perPage* fields assigned just above)
    // @ts-ignore - Splide is loaded from CDN
    this.splide = new window.Splide(this.splideElement, {
      type: useLoop ? 'loop' : 'slide',
      perPage: this.fractionalMode ? 1 : perPageDesktop,
      perMove: 1,
      autoWidth: this.fractionalMode,
      autoHeight: true,
      arrows: false,
      pagination: false,
      autoplay: false,
      transition: 'slide',
      speed: 600,
      // Drag must be gated on the perPage in effect at the CURRENT viewport,
      // not the mobile one. Gating on perPageMobile meant a section whose
      // mobile perPage had been clamped up to slideCount lost drag at every
      // breakpoint, desktop included. Recomputed on resize in toggleControls().
      drag: slideCount > this.currentPerPage(),
      touchMinDistance: 5,
      // Splide's own wheel handling reacts to vertical deltas too, which stops
      // the page scrolling once the cursor is over the carousel. Left off; a
      // sideways-only handler lives in attachHorizontalWheelGuard().
      wheel: false,
      keyboard: 'global',
      gap: gapDesktop + 'px',
      padding: 0,
      updateOnMove: true,
      trimSpace: 'move',
      breakpoints: {
        1024: { perPage: this.fractionalMode ? 1 : perPageTablet },
        768: { perPage: this.fractionalMode ? 1 : perPageMobile, gap: gapMobile + 'px' }
      }
    });

    this.slideCount = slideCount;
    this._fixing = false;
    this.splide.mount();
    this.attachHorizontalWheelGuard();
    this.attachCustomNavigation();
    this.attachScrollbar();
    this.toggleControls();
    this.fixLastSlideMargin();

    // Debounced window-resize handler only. Do NOT hook Splide's 'resize'
    // or 'refresh' events, because calling splide.refresh() inside the
    // handler would re-fire them and cause infinite recursion.
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.toggleControls();
        this.fixLastSlideMargin();
      }, 100);
    });
  }

  /**
   * Splide's `wheel` option reacts to vertical deltas as well as horizontal
   * ones, which would swallow normal page scrolling over the carousel. Capture
   * wheel events first and stop any gesture that is mostly vertical from
   * reaching Splide, so a two-finger sideways swipe moves the slides while
   * up/down scrolling still scrolls the page.
   */
  attachHorizontalWheelGuard() {
    if (!this.splideElement) return;
    const track = this.splideElement.querySelector('.splide__track');
    if (!track) return;

    track.addEventListener(
      'wheel',
      (e) => {
        if (!this.splide) return;
        if (this.splideElement.classList.contains('is-fits')) return;

        // Only act on a clearly sideways two-finger swipe. Anything with a
        // meaningful vertical component is left entirely alone so the page
        // keeps scrolling as normal — the carousel never traps the cursor.
        if (Math.abs(e.deltaX) < Math.abs(e.deltaY) * 2) return;
        if (Math.abs(e.deltaX) < 8) return;

        e.preventDefault();

        const now = Date.now();
        if (this._wheelLock && now - this._wheelLock < 350) return;
        this._wheelLock = now;

        this.splide.go(e.deltaX > 0 ? '>' : '<');
      },
      { passive: false }
    );
  }

  /**
   * The perPage value in effect at the current viewport width. Mirrors the
   * breakpoints passed to Splide (1024 tablet, 768 mobile) so drag/controls
   * decisions agree with what is actually rendered.
   */
  currentPerPage() {
    if (window.innerWidth <= 768) return this.perPageMobile;
    if (window.innerWidth <= 1024) return this.perPageTablet;
    return this.perPageDesktop;
  }

  /**
   * When all slides fit (no scrolling), Splide still adds margin-right to
   * the last slide which pushes its width past the container.
   * Remove the trailing margin so the last card aligns flush right.
   * In fractional mode, also set explicit slide widths.
   */
  fixLastSlideMargin() {
    if (!this.splide || !this.splideElement) return;
    if (this._fixing) return; // prevent re-entry
    this._fixing = true;

    try {
      // Determine effective perPage at the current viewport
      let effectivePerPage = this.perPageDesktop;
      let effectiveGap = this.gapDesktop;
      if (window.innerWidth <= 768) {
        effectivePerPage = this.perPageMobile;
        effectiveGap = this.gapMobile;
      } else if (window.innerWidth <= 1024) {
        effectivePerPage = this.perPageTablet;
      }

      const allSlides = this.splideElement.querySelectorAll('.splide__slide');
      if (this.fractionalMode) {
        const track = this.splideElement.querySelector('.splide__track');
        const trackWidth = track ? track.clientWidth : 0;
        if (trackWidth > 0) {
          // Splide advances the track by (width + gap) per slide — the gap is a
          // margin-right carried by EVERY slide, including the last one on screen.
          // So N visible slides consume N*(width + gap), not N*width + (N-1)*gap.
          // Subtracting only (N-1) gaps here made each card gap/N too wide, which
          // overflowed the track and clipped the last card in view.
          const slideWidth = (trackWidth - effectivePerPage * effectiveGap) / effectivePerPage;
          allSlides.forEach((slide) => {
            slide.style.width = slideWidth + 'px';
          });
        }
      }

      const fits = this.slideCount <= Math.floor(effectivePerPage);
      const slides = this.splideElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
      slides.forEach((slide, i) => {
        const shouldZero = fits && i === slides.length - 1;
        const currentMargin = slide.style.marginRight;
        if (shouldZero) {
          if (currentMargin !== '0px' && currentMargin !== '0') {
            slide.style.marginRight = '0px';
          }
        } else if (!this.fractionalMode && currentMargin !== '') {
          slide.style.removeProperty('margin-right');
        }
      });
    } finally {
      this._fixing = false;
    }
  }

  /**
   * Hide nav arrows + scrollbar when all slides fit in the visible area
   * at the current breakpoint (no scrolling needed).
   */
  toggleControls() {
    if (!this.splide || !this.splideElement) return;
    const section = this.splideElement.closest('.section-real-reviews');
    if (!section) return;

    // In fractional mode splide.options.perPage is pinned to 1 (autoWidth does
    // the real work), so it can't be used to decide whether everything fits.
    // Use the breakpoint-aware value instead.
    const currentPerPage = this.currentPerPage() || 1;
    const fits = this.slideCount <= currentPerPage;

    this.splideElement.classList.toggle('is-fits', fits);

    // Keep drag in sync with the current breakpoint: enabled whenever there is
    // something off-screen to drag to.
    this.splide.options = { drag: !fits };

    const bottomRow = section.querySelector('.reviews-bottom-row');
    const nav = section.querySelector('.reviews-navigation');
    const scrollbar = section.querySelector('.reviews-scrollbar');

    if (bottomRow instanceof HTMLElement) {
      bottomRow.style.display = fits ? 'none' : '';
    } else {
      if (nav instanceof HTMLElement) nav.style.display = fits ? 'none' : '';
      if (scrollbar instanceof HTMLElement) scrollbar.style.display = fits ? 'none' : '';
    }
  }

  /**
   * Attach custom navigation button functionality
   */
  attachCustomNavigation() {
    if (this.prevButton instanceof HTMLElement) {
      this.prevButton.addEventListener('click', () => {
        if (this.splide) this.splide.go('<');
      });
    }

    if (this.nextButton instanceof HTMLElement) {
      this.nextButton.addEventListener('click', () => {
        if (this.splide) this.splide.go('>');
      });
    }

    // Update button states based on carousel position
    this.updateNavigationState();
    if (this.splide) {
      this.splide.on('move', () => this.updateNavigationState());
    }
  }

  /**
   * Update navigation button disabled state
   */
  updateNavigationState() {
    if (!this.prevButton || !this.nextButton || !this.splide) return;

    const index = this.splide.index;
    const maxIndex = this.splide.length - this.splide.options.perPage;

    // For slide type, disable buttons at endpoints
    if (this.splide.options.type !== 'loop') {
      if (this.prevButton instanceof HTMLElement) {
        if (index <= 0) {
          this.prevButton.style.opacity = '0.5';
          this.prevButton.style.pointerEvents = 'none';
        } else {
          this.prevButton.style.opacity = '1';
          this.prevButton.style.pointerEvents = 'auto';
        }
      }

      if (this.nextButton instanceof HTMLElement) {
        if (index >= maxIndex) {
          this.nextButton.style.opacity = '0.5';
          this.nextButton.style.pointerEvents = 'none';
        } else {
          this.nextButton.style.opacity = '1';
          this.nextButton.style.pointerEvents = 'auto';
        }
      }
    }
  }

  /**
   * Attach scrollbar that tracks carousel position
   */
  attachScrollbar() {
    if (!this.splide || !this.splideElement) return;
    const section = this.splideElement.closest('.section-real-reviews');
    if (!section) return;

    const thumb = section.querySelector('.reviews-scrollbar-thumb');
    if (!thumb) return;

    const updateThumb = () => {
      const total = this.splide.length;
      const index = this.splide.index;
      const thumbWidth = Math.max((1 / total) * 100, 10);
      const progress = index / (total - 1);
      const maxLeft = 100 - thumbWidth;
      thumb.style.width = thumbWidth + '%';
      thumb.style.left = (progress * maxLeft) + '%';
    };

    updateThumb();
    this.splide.on('move', updateThumb);
    this.splide.on('resize', updateThumb);
  }

  /**
   * Destroy carousel instance
   */
  destroy() {
    if (this.splide) {
      this.splide.destroy();
    }
  }
}

/**
 * Initialize all reviews slider instances on the page
 */
function initAllReviewsSliders() {
  const carousels = document.querySelectorAll('.section-real-reviews .splide:not(.is-initialized)');
  carousels.forEach((carousel) => {
    new ReviewsSlider(carousel);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllReviewsSliders);
} else {
  initAllReviewsSliders();
}

// Re-initialize on Shopify section reload (theme editor)
document.addEventListener('shopify:section:load', function(event) {
  var section = event.target;
  if (section && section.querySelector('.reviews-splide')) {
    var carousels = section.querySelectorAll('.reviews-splide');
    carousels.forEach(function(carousel) {
      new ReviewsSlider(carousel);
    });
  }
});

// Export for manual initialization (useful for Shopify sections)
window.ReviewsSlider = ReviewsSlider;
window.initAllReviewsSliders = initAllReviewsSliders;
}
